import {
  CreateLiabilityPayload,
  LiabilitiesDetails,
  LiabilitiesResponse,
  LiabilityBalanceHistory,
  LiabilityInterestRatePayload,
  LiabilityPayment,
  LiabilityStatus,
  LiabilityTypes,
  LiabilityUpdateRequest,
  UpdateLiabilityPayment,
} from "../../pages/Liabilities";
import { baseApi, transactionRipple } from "../api";

interface GetLiabilitiesArgs {
  activeOnly?: boolean;
  year?: number;
}

interface UpdateLiabilityArgs {
  id: number;
  payload: LiabilityUpdateRequest;
}

interface UpdateLiabilityStatusArgs {
  id: number;
  status: LiabilityStatus;
}

interface UpdateLiabilityAccountStatusArgs {
  id: number;
  isActive: boolean;
}

interface AddLiabilityPaymentArgs {
  id: number;
  payload: LiabilityPayment;
}

interface UpdateLiabilityPaymentArgs {
  id: number;
  paymentId: number;
  payload: UpdateLiabilityPayment;
}

interface DeleteLiabilityPaymentArgs {
  id: number;
  paymentId: number;
}

interface CreateInterestRateArgs {
  id: number;
  payload: LiabilityInterestRatePayload;
}

interface UpdateInterestRateArgs {
  id: number;
  interestRateId: number;
  payload: LiabilityInterestRatePayload;
}

interface DeleteInterestRateArgs {
  id: number;
  interestRateId: number;
}

const liabilitiesListTags = (result?: LiabilitiesResponse) => [
  { type: "Liability" as const, id: "LIST" },
  ...(result?.liabilities.map((liability) => ({
    type: "Liability" as const,
    id: liability.id,
  })) ?? []),
];

const invalidateLiability = (id: number) => [
  { type: "Liability" as const, id },
  { type: "Liability" as const, id: "LIST" },
  { type: "LiabilityBalanceHistory" as const, id },
  "LiabilityYears" as const,
  "Finances" as const,
];

// An interest-rate edit moves no money, so it deliberately skips the transaction ripple.
const invalidateLiabilitySubResource = (id: number) => [
  { type: "Liability" as const, id },
  { type: "Liability" as const, id: "LIST" },
  { type: "LiabilityBalanceHistory" as const, id },
  "Finances" as const,
];

// A loan payment is a real transaction that moves account money, so it fires the full cross-domain ripple.
const invalidateLiabilityPayment = (id: number) => [
  { type: "Liability" as const, id },
  { type: "Liability" as const, id: "LIST" },
  ...transactionRipple(),
];

export const liabilitiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLiabilities: builder.query<
      LiabilitiesResponse,
      GetLiabilitiesArgs | void
    >({
      query: (args) => {
        const { activeOnly = true, year } = args ?? {};
        return {
          url: "/liabilities",
          params: year === undefined ? { activeOnly } : { activeOnly, year },
        };
      },
      providesTags: liabilitiesListTags,
    }),

    getLiabilitiesYears: builder.query<number[], void>({
      query: () => ({ url: "/liabilities/years" }),
      providesTags: ["LiabilityYears"],
      keepUnusedDataFor: 300,
    }),

    getLiability: builder.query<LiabilitiesDetails, number>({
      query: (id) => ({ url: "/liabilities/" + id }),
      providesTags: (_result, _error, id) => [{ type: "Liability", id }],
    }),

    getLiabilityBalanceHistory: builder.query<LiabilityBalanceHistory, number>({
      query: (id) => ({ url: "/liabilities/" + id + "/balance-history" }),
      providesTags: (_result, _error, id) => [
        { type: "LiabilityBalanceHistory", id },
      ],
    }),

    getLiabilityTypes: builder.query<LiabilityTypes[], void>({
      query: () => ({ url: "/liabilities/types" }),
      providesTags: ["LiabilityTypes"],
      keepUnusedDataFor: 300,
    }),

    createLiability: builder.mutation<unknown, CreateLiabilityPayload>({
      query: (payload) => ({
        url: "/liabilities",
        method: "post",
        data: payload,
      }),
      invalidatesTags: [
        { type: "Liability", id: "LIST" },
        "LiabilityYears",
        "Finances",
      ],
    }),

    updateLiability: builder.mutation<unknown, UpdateLiabilityArgs>({
      query: ({ id, payload }) => ({
        url: "/liabilities/" + id,
        method: "patch",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) => invalidateLiability(id),
    }),

    updateLiabilityStatus: builder.mutation<void, UpdateLiabilityStatusArgs>({
      query: ({ id, status }) => ({
        url: "/liabilities/" + id,
        method: "patch",
        data: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => invalidateLiability(id),
    }),

    updateLiabilityAccountStatus: builder.mutation<
      void,
      UpdateLiabilityAccountStatusArgs
    >({
      query: ({ id, isActive }) => ({
        url: "/liabilities/" + id,
        method: "patch",
        data: { isActive },
      }),
      // Detail only: the list has many cache entries and `isActive` changes membership, so it refetches instead.
      async onQueryStarted({ id, isActive }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          liabilitiesApi.util.updateQueryData("getLiability", id, (draft) => {
            draft.isActive = isActive;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { id }) => invalidateLiability(id),
    }),

    deleteLiability: builder.mutation<unknown, number>({
      query: (id) => ({
        url: "/liabilities/" + id,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, id) => invalidateLiability(id),
    }),

    addLiabilityPayment: builder.mutation<unknown, AddLiabilityPaymentArgs>({
      query: ({ id, payload }) => ({
        url: "/liabilities/" + id + "/payments",
        method: "post",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateLiabilityPayment(id),
    }),

    updateLiabilityPayment: builder.mutation<
      unknown,
      UpdateLiabilityPaymentArgs
    >({
      query: ({ id, paymentId, payload }) => ({
        url: "/liabilities/" + id + "/payments/" + paymentId,
        method: "put",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateLiabilityPayment(id),
    }),

    deleteLiabilityPayment: builder.mutation<void, DeleteLiabilityPaymentArgs>({
      query: ({ id, paymentId }) => ({
        url: "/liabilities/" + id + "/payments/" + paymentId,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateLiabilityPayment(id),
    }),

    createInterestRate: builder.mutation<unknown, CreateInterestRateArgs>({
      query: ({ id, payload }) => ({
        url: "/liabilities/" + id + "/interest-rates",
        method: "post",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateLiabilitySubResource(id),
    }),

    updateInterestRate: builder.mutation<unknown, UpdateInterestRateArgs>({
      query: ({ id, interestRateId, payload }) => ({
        url: "/liabilities/" + id + "/interest-rates/" + interestRateId,
        method: "put",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateLiabilitySubResource(id),
    }),

    deleteInterestRate: builder.mutation<unknown, DeleteInterestRateArgs>({
      query: ({ id, interestRateId }) => ({
        url: "/liabilities/" + id + "/interest-rates/" + interestRateId,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, { id }) =>
        invalidateLiabilitySubResource(id),
    }),
  }),
});

export const {
  useGetLiabilitiesQuery,
  useGetLiabilitiesYearsQuery,
  useGetLiabilityQuery,
  useGetLiabilityBalanceHistoryQuery,
  useGetLiabilityTypesQuery,
  useCreateLiabilityMutation,
  useUpdateLiabilityMutation,
  useUpdateLiabilityStatusMutation,
  useUpdateLiabilityAccountStatusMutation,
  useDeleteLiabilityMutation,
  useAddLiabilityPaymentMutation,
  useUpdateLiabilityPaymentMutation,
  useDeleteLiabilityPaymentMutation,
  useCreateInterestRateMutation,
  useUpdateInterestRateMutation,
  useDeleteInterestRateMutation,
} = liabilitiesApi;
